type ThemeId = "system" | "light" | "dark" | "sepia";

interface Theme {
    name: ThemeId;
    title: string;
}


export class ThemeStore {
    private static _instance: ThemeStore = new ThemeStore();
    public static get instance() { return this._instance; }
    private _eventTarget: EventTarget = new EventTarget();

    public addEventListener = (type: string, listener: EventListenerOrEventListenerObject) => {
        this._eventTarget.addEventListener(type, listener);
    }

    private static readonly themes: Theme[] = [{
        name: "system",
        title: "Follow System"
    }, {
        name: "light",
        title: "Light"
    }, {
        name: "dark",
        title: "Dark"
    }
    // , {
    //     name: "sepia",
    //     title: "Sepia"
    // }
];

    constructor() {
        // see if something is set in local storage
        const savedTheme = localStorage.getItem("theme:current");
        if (savedTheme && this.isValid(savedTheme) && savedTheme !== "system") {
            document.documentElement.setAttribute("theme", savedTheme);
            return;
        }

        // detect system theme preference
        this.setSystemTheme();
        
        // listen for changes
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.setSystemTheme);
    }

    private setSystemTheme = () => {
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

        // if browser has ?force=<theme> take that instead
        // allow query-string override: ?force=<theme> or ?theme=<theme>
        try {
            const params = new URLSearchParams(window.location.search);
            const forced = (params.get("force") || params.get("theme")) || null;
            if (forced && this.isValid(forced)) {
                if (forced !== "system") {
                    document.documentElement.setAttribute("theme", forced as ThemeId);
                    this._eventTarget.dispatchEvent(new Event("theme-changed"));
                    return;
                }
                // if forced === "system" fall through to system detection
            }
        } catch (e) {
            // ignore URL parsing errors and fall back to system preference
        }

        if (prefersDark) {
            document.documentElement.setAttribute("theme", "dark");
        } else {
            document.documentElement.setAttribute("theme", "light");
        }
        this._eventTarget.dispatchEvent(new Event("theme-changed"));
    }

    public get isDark(): boolean {
        return document.documentElement.getAttribute("theme") === "dark";
    }

    public get current() : ThemeId {
        const theme = localStorage.getItem("theme:current");
        if (this.isValid(theme))
            return theme as ThemeId;

        return "system";
    }

    public set current(theme: ThemeId) {
        if (!this.isValid(theme))
            return;

        if (theme === "system") {
            this.setSystemTheme();
            localStorage.removeItem("theme:current");
            return;
        }

        document.documentElement.setAttribute("theme", theme);
        localStorage.setItem("theme:current", theme);
        this._eventTarget.dispatchEvent(new Event("theme-changed"));
    }

    public all() : { name: string; title: string; current: boolean }[] {
        return ThemeStore.themes.map(theme => ({
            name: theme.name,
            title: theme.title,
            current: this.current === theme.name
        }));
    }

    private isValid = (theme: string) => {
        const validThemes = ["system", "light", "dark", "sepia"];
        return validThemes.includes(theme);
    }
}